import React, { useState, useEffect } from 'react';
import {
  ShieldCheckIcon,
  ChartBarIcon,
  UserGroupIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import toast from 'react-hot-toast';
import { api } from '../../services/authService';

const CreditMetricsWidget = () => {
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMetrics();
  }, []);

  const loadMetrics = async () => {
    try {
      setLoading(true);

      // Buscar todos os clientes com análise de crédito
      const response = await api.get('/api/clients');
      const data = response.data;
      const clients = data.data || [];

      // Calcular métricas
      const withScore = clients.filter(c => c.credit_score !== null && c.credit_score !== undefined);
      const byRisk = {
        very_low: withScore.filter(c => c.risk_level === 'very_low').length,
        low: withScore.filter(c => c.risk_level === 'low').length,
        medium: withScore.filter(c => c.risk_level === 'medium').length,
        high: withScore.filter(c => c.risk_level === 'high').length,
        very_high: withScore.filter(c => c.risk_level === 'very_high').length
      };

      const avgScore = withScore.length > 0
        ? Math.round(withScore.reduce((sum, c) => sum + c.credit_score, 0) / withScore.length)
        : 0;

      setMetrics({
        total_clients: clients.length,
        analyzed_clients: withScore.length,
        pending_analysis: clients.length - withScore.length,
        avg_score: avgScore,
        by_risk: byRisk
      });
    } catch (error) {
      console.error('Erro ao carregar métricas:', error);
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Faça login novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const getRiskLevelLabel = (level) => {
    const labels = {
      'very_low': 'Muito Baixo',
      'low': 'Baixo',
      'medium': 'Médio',
      'high': 'Alto',
      'very_high': 'Muito Alto'
    };
    return labels[level] || level;
  };

  const getRiskLevelColor = (level) => {
    const colors = {
      'very_low': '#22c55e',
      'low': '#3b82f6',
      'medium': '#f59e0b',
      'high': '#f97316',
      'very_high': '#ef4444'
    };
    return colors[level] || colors.medium;
  };

  if (loading) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
        <div className="text-center text-gray-600 dark:text-gray-400">
          Carregando métricas...
        </div>
      </div>
    );
  }

  if (!metrics) {
    return null;
  }

  // Preparar dados para o gráfico de pizza
  const chartData = Object.keys(metrics.by_risk)
    .filter(key => metrics.by_risk[key] > 0)
    .map(key => ({
      name: getRiskLevelLabel(key),
      value: metrics.by_risk[key],
      color: getRiskLevelColor(key)
    }));

  const COLORS = chartData.map(d => d.color);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="flex items-center gap-3 mb-6">
        <ChartBarIcon className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Métricas de Análise de Crédito
        </h3>
      </div>

      {/* Cards de Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <UserGroupIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            <p className="text-xs font-medium text-blue-800 dark:text-blue-300">
              Total de Clientes
            </p>
          </div>
          <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
            {metrics.total_clients}
          </p>
        </div>

        <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheckIcon className="w-5 h-5 text-green-600 dark:text-green-400" />
            <p className="text-xs font-medium text-green-800 dark:text-green-300">
              Analisados
            </p>
          </div>
          <p className="text-2xl font-bold text-green-900 dark:text-green-100">
            {metrics.analyzed_clients}
          </p>
          <p className="text-xs text-green-600 dark:text-green-400 mt-1">
            {metrics.total_clients > 0
              ? Math.round((metrics.analyzed_clients / metrics.total_clients) * 100)
              : 0}% do total
          </p>
        </div>

        <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
            <p className="text-xs font-medium text-yellow-800 dark:text-yellow-300">
              Pendentes
            </p>
          </div>
          <p className="text-2xl font-bold text-yellow-900 dark:text-yellow-100">
            {metrics.pending_analysis}
          </p>
        </div>

        <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2">
            <ChartBarIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" />
            <p className="text-xs font-medium text-purple-800 dark:text-purple-300">
              Score Médio
            </p>
          </div>
          <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
            {metrics.avg_score}
          </p>
          <p className="text-xs text-purple-600 dark:text-purple-400 mt-1">
            de 100 pontos
          </p>
        </div>
      </div>

      {/* Gráfico de Distribuição de Risco */}
      {chartData.length > 0 ? (
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
            Distribuição por Nível de Risco
          </h4>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={90}
                paddingAngle={5}
                dataKey="value"
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index]} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--card-bg)',
                  border: '1px solid var(--border-color)',
                  borderRadius: '8px'
                }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>

          {/* Legenda Detalhada */}
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-2">
            {Object.keys(metrics.by_risk).map((key) => {
              const count = metrics.by_risk[key];
              if (count === 0) return null;

              return (
                <div
                  key={key}
                  className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-900"
                >
                  <div
                    className="w-3 h-3 rounded-full flex-shrink-0"
                    style={{ backgroundColor: getRiskLevelColor(key) }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                      {getRiskLevelLabel(key)}
                    </p>
                    <p className="text-xs text-gray-600 dark:text-gray-400">
                      {count} {count === 1 ? 'cliente' : 'clientes'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <ShieldCheckIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p className="text-sm">Nenhuma análise de crédito realizada ainda</p>
          <p className="text-xs mt-1">
            As métricas aparecerão após realizar análises de crédito dos clientes
          </p>
        </div>
      )}
    </div>
  );
};

export default CreditMetricsWidget;
